import React, { useState, useEffect, useMemo } from "react";
import { makeStyles, createStyles, Theme } from "@material-ui/core/styles";
import { Typography, Chip } from "@material-ui/core";
import { ChangeKind, parseChangelog } from "./changelog";
import { KIND_STYLE } from "./ChangelogVersionView";

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    title: {
      fontSize: 11,
      fontWeight: 700,
      textTransform: "uppercase",
      color: theme.palette.text.disabled,
      marginBottom: theme.spacing(1.5)
    },
    item: {
      display: "flex",
      alignItems: "center",
      gap: theme.spacing(1),
      padding: theme.spacing(0.75, 1.25),
      borderRadius: 8,
      cursor: "pointer",
      marginBottom: theme.spacing(0.5),
      border: `1px solid ${theme.palette.divider}`,
      transition: "all 0.2s",
      "&:hover": {
        borderColor: theme.palette.primary.main,
        backgroundColor: theme.palette.action.hover
      }
    },
    itemActive: {
      borderColor: theme.palette.primary.main,
      backgroundColor: theme.palette.primary.main + "10"
    },
    versionChip: {
      fontWeight: 700
    },
    versionChipActive: {
      backgroundColor: theme.palette.primary.main,
      color: "#fff"
    },
    dateLabel: {
      flex: 1,
      fontSize: 12,
      color: theme.palette.text.disabled
    },
    counts: {
      display: "flex",
      gap: 4,
      flexWrap: "wrap"
    },
    count: {
      fontSize: 11,
      fontWeight: 700,
      borderRadius: 10,
      padding: "0 7px",
      lineHeight: "18px"
    }
  })
);

interface VersionEntry {
  version: string;
  date: string;
}

interface ChangelogSidebarProps {
  content: string;
  /** Version to start on (the new-version banner opens the docs on it); the newest by default */
  initialVersion?: string;
}

function parseVersions(markdown: string): VersionEntry[] {
  const entries: VersionEntry[] = [];
  const regex = /^## \[([^\]]+)\](?:\s*[—–-]\s*(.+))?/gm;
  let match: RegExpExecArray | null = regex.exec(markdown);

  while (match !== null) {
    entries.push({
      version: match[1],
      date: match[2]?.trim() || ""
    });
    match = regex.exec(markdown);
  }

  return entries;
}

const ChangelogSidebar: React.FC<ChangelogSidebarProps> = ({ content, initialVersion }) => {
  const classes = useStyles();
  const versions = parseVersions(content);
  const [activeVersion, setActiveVersion] = useState<string>(
    (initialVersion && versions.some((v) => v.version === initialVersion) ? initialVersion : versions[0]?.version) || ""
  );
  // items per kind of change, so each version shows at a glance what it brought
  const countsByVersion = useMemo(() => {
    const result: Record<string, { kind: ChangeKind; count: number }[]> = {};

    parseChangelog(content).forEach((entry) => {
      const kinds = (Object.keys(KIND_STYLE) as ChangeKind[]).map((kind) => ({
        kind,
        count: entry.sections.filter((s) => s.kind === kind).reduce((sum, s) => sum + s.items.length, 0)
      }));

      result[entry.version] = kinds.filter((k) => k.count > 0);
    });

    return result;
  }, [content]);

  useEffect(() => {
    if (versions.length > 0 && !activeVersion) {
      setActiveVersion(versions[0].version);
    }
  }, [versions, activeVersion]);

  const handleClick = (version: string) => {
    setActiveVersion(version);
    window.dispatchEvent(new CustomEvent("changelog-version-select", { detail: version }));
  };

  if (versions.length === 0) return null;

  return (
    <div>
      <Typography className={classes.title}>Versões</Typography>
      {versions.map((entry) => (
        <div
          key={entry.version}
          className={`${classes.item} ${activeVersion === entry.version ? classes.itemActive : ""}`}
          onClick={() => handleClick(entry.version)}
        >
          <Chip
            label={`v${entry.version}`}
            size="small"
            className={`${classes.versionChip} ${
              activeVersion === entry.version ? classes.versionChipActive : ""
            }`}
          />
          {entry.date && <div className={classes.dateLabel}>{entry.date}</div>}
          {(countsByVersion[entry.version] || []).length > 0 && (
            <div className={classes.counts}>
              {countsByVersion[entry.version].map(({ kind, count }) => (
                <span
                  key={kind}
                  className={classes.count}
                  style={{ color: KIND_STYLE[kind].color, backgroundColor: KIND_STYLE[kind].background }}
                  title={`${count} ${count === 1 ? KIND_STYLE[kind].one : KIND_STYLE[kind].many}`}
                >
                  {count}
                </span>
              ))}
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

export default ChangelogSidebar;
